<!--
📌 Componente: VitrineModal
📂 Caminho: src/components/VitrineModal.vue
📝 Descrição: Modal de tela cheia para exibir textos legais
  (Impressum/Datenschutz) com i18n e acessibilidade básica.

👁️ Interface e Interação com o Usuário:
  - Overlay semitransparente
  - Conteúdo em painel central rolável
  - Botão fechar com ícone inline
  - Fechar com tecla Escape

📥 Entrada (Props):
  - `modelValue` (Boolean) - controle de visibilidade (v-model)
  - `type` ("impressum"|"datenschutz") - define o conteúdo i18n
  - `titleKey` (String|undefined) - sobrepõe o título i18n opcional

📤 Saída (Emits):
  - `update:modelValue` - fecha ao clicar em overlay/btn/Escape
  - `closed` - emitido após fechar (para rastreamento opcional)

🔗 Dependências:
  - **Componentes Vue:** N/A
  - **Utilitários JS:** N/A
  - **Backend:** N/A
  - **Bibliotecas externas:** TailwindCSS (cores/fontes do projeto)
-->

<template>
  <transition name="fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[9999] flex items-stretch"
      role="dialog"
      :aria-label="computedTitle"
      aria-modal="true"
      @keydown.esc.prevent.stop="close"
    >
      <!-- Overlay -->
      <div class="absolute inset-0 bg-cinza-900/50" @click="close"></div>

      <!-- Conteúdo -->
      <div
        ref="panel"
        class="relative mx-auto my-4 w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-branco text-azul-profundo rounded shadow-xl flex flex-col focus:outline-none focus:ring-2 focus:ring-azul-profundo"
        role="document"
        tabindex="0"
        @click.stop
      >
        <!-- Cabeçalho -->
        <header
          class="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-cinza-200"
        >
          <h2 class="font-roboto text-h3">
            {{ computedTitle }}
          </h2>

          <button
            class="p-2 rounded-full hover:bg-cinza-claro focus:outline-none focus:ring-2 focus:ring-azul-profundo"
            type="button"
            :aria-label="$t('vitrine.legal.actions.close')"
            @click="close"
          >
            <!-- Ícone fechar (inline) -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </header>

        <!-- Corpo rolável -->
        <section
          class="px-4 sm:px-6 py-4 overflow-y-auto"
          :aria-labelledby="computedTitle"
        >
          <!-- Blocos do Impressum -->
          <template v-if="type === 'impressum'">
            <p class="mb-4 text-body">
              {{ $t("vitrine.legal.impressum.intro") }}
            </p>

            <h3 class="mt-6 mb-2 font-roboto text-h3">
              {{ $t("vitrine.legal.sections.company") }}
            </h3>
            <ul class="space-y-1 text-body">
              <li>{{ $t("vitrine.legal.company.name") }}</li>
              <li>{{ $t("vitrine.legal.company.address") }}</li>
              <li>
                {{
                  $t("vitrine.legal.company.contact", {
                    mail: "info@praxisgmbh.de",
                  })
                }}
              </li>
              <li>{{ $t("vitrine.legal.company.register") }}</li>
              <li>{{ $t("vitrine.legal.company.vat") }}</li>
            </ul>

            <h3 class="mt-6 mb-2 font-roboto text-h3">
              {{ $t("vitrine.legal.sections.liability") }}
            </h3>
            <p class="text-body">
              {{ $t("vitrine.legal.impressum.liabilityNote") }}
            </p>
          </template>

          <!-- Blocos do Datenschutz -->
          <template v-else-if="type === 'datenschutz'">
            <p class="mb-4 text-body">
              {{ $t("vitrine.legal.datenschutz.intro") }}
            </p>

            <h3 class="mt-6 mb-2 font-roboto text-h3">
              {{ $t("vitrine.legal.sections.controller") }}
            </h3>

            <ul class="space-y-1 text-body">
              <li>{{ $t("vitrine.legal.company.name") }}</li>
              <li>{{ $t("vitrine.legal.company.address") }}</li>
              <li>
                {{
                  $t("vitrine.legal.company.contact", {
                    mail: "info@praxisgmbh.de",
                  })
                }}
              </li>
            </ul>

            <h3 class="mt-6 mb-2 font-roboto text-h3">
              {{ $t("vitrine.legal.sections.processing") }}
            </h3>
            <p class="text-body">
              {{ $t("vitrine.legal.datenschutz.processing") }}
            </p>

            <h3 class="mt-6 mb-2 font-roboto text-h3">
              {{ $t("vitrine.legal.sections.rights") }}
            </h3>
            <ul class="list-disc pl-6 space-y-1 text-body">
              <li>{{ $t("vitrine.legal.datenschutz.rights.access") }}</li>
              <li>{{ $t("vitrine.legal.datenschutz.rights.erase") }}</li>
              <li>{{ $t("vitrine.legal.datenschutz.rights.portability") }}</li>
              <li>{{ $t("vitrine.legal.datenschutz.rights.object") }}</li>
              <li>{{ $t("vitrine.legal.datenschutz.rights.complaint") }}</li>
            </ul>

            <h3 class="mt-6 mb-2 font-roboto text-h3">
              {{ $t("vitrine.legal.sections.dpo") }}
            </h3>
            <p class="text-body">
              {{
                $t("vitrine.legal.datenschutz.dpo", {
                  mail: "datenschutz@gdsm.de",
                })
              }}
            </p>
          </template>

          <!-- Fallback -->
          <template v-else>
            <slot>
              <p class="text-body">
                {{ $t("vitrine.legal.fallback") }}
              </p>
            </slot>
          </template>
        </section>

        <!-- Rodapé -->
        <footer
          class="px-4 sm:px-6 py-3 border-t border-cinza-200 flex justify-end"
        >
          <button
            class="px-4 py-2 bg-azul-profundo text-branco rounded hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-azul-profundo"
            type="button"
            @click="close"
          >
            {{ $t("vitrine.legal.actions.close") }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * 📌 Script: VitrineModal.vue
 * 📂 Caminho: src/components/VitrineModal.vue
 * 📝 Descrição: Modal legal fullscreen (Impressum/Datenschutz)
 *
 * 🔄 Relação com o Projeto:
 *  - Usado na Vitrine pública via links do rodapé.
 *  - Textos via i18n (de como fallback).
 *
 * 📥 Entradas (Parâmetros):
 *  - `modelValue` (Boolean) - controle de visibilidade (v-model)
 *  - `type` ("impressum"|"datenschutz") - tipo de conteúdo
 *  - `titleKey` (String|undefined) - chave i18n de título opcional
 *
 * 📤 Saídas:
 *  - `update:modelValue` (Boolean) - fecha/abre o modal
 *  - `closed` (void) - emitido ao fechar
 *
 * 🧩 Exports:
 *  - default (componente Vue)
 *
 * 🔗 Dependências:
 *  - **Tailwind:** classes conforme paleta e tipografia do projeto
 */
import { onMounted, onBeforeUnmount, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";

export default {
  name: "VitrineModal",
  props: {
    modelValue: { type: Boolean, required: true },
    type: {
      type: String,
      default: "impressum",
      validator: (v) => ["impressum", "datenschutz", "custom"].includes(v),
    },
    titleKey: { type: String, default: "" },
    rawTitle: { type: String, default: "" },
  },
  emits: ["update:modelValue", "closed"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const panel = ref(null);
    const lastFocus = ref(null);

    // Bloqueia scroll do body quando aberto
    watch(
      () => props.modelValue,
      (open) => {
        document.body.style.overflow = open ? "hidden" : "";
        if (open) {
          lastFocus.value = document.activeElement;
        }
      },
      { immediate: true }
    );

    const computedTitle = computed(() => {
      if (props.rawTitle) return props.rawTitle;
      if (props.titleKey) return t(props.titleKey);
      if (props.type === "custom") return "";
      return props.type === "impressum"
        ? t("vitrine.legal.impressum.title")
        : t("vitrine.legal.datenschutz.title");
    });

    const focusPanel = () => {
      // Foca painel para acessibilidade e Escape
      panel.value && panel.value.focus();
    };

    const close = () => {
      emit("update:modelValue", false);
      emit("closed");
      // Restaura foco ao elemento que abriu o modal
      setTimeout(() => {
        if (lastFocus.value && lastFocus.value.focus) {
          lastFocus.value.focus();
        }
      }, 0);
    };

    const onKey = (e) => {
      if (e.key === "Escape") close();
    };

    onMounted(() => {
      window.addEventListener("keydown", onKey);
      if (props.modelValue) focusPanel();
    });

    onBeforeUnmount(() => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    });

    watch(
      () => props.modelValue,
      (open) => open && focusPanel()
    );

    return { panel, computedTitle, close, t };
  },
};
</script>

<style scoped>
/* Transição simples de fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
